import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import socketService from '../services/socket.service';
import CookieUtils from '../utils/cookie-utils';
import { axiosInstance } from '../hooks/useFetch';
import { ApiConfig, API_PUBLIC_ROUTER } from '../services/api-config';

interface SocketContextType {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  realtimeUrl: string | null;
  reconnect: () => Promise<void>;
}

const SocketContext = createContext<SocketContextType>({
  isConnected: false,
  isConnecting: false,
  error: null,
  realtimeUrl: null,
  reconnect: async () => {},
});

export const useSocketContext = () => useContext(SocketContext);

const MAX_RETRY_ATTEMPTS = 5;
const RETRY_BASE_DELAY_MS = 5000;
const RETRY_MAX_DELAY_MS = 60000;

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [realtimeUrl, setRealtimeUrl] = useState<string | null>(null);
  const mountedRef = useRef(false);
  // Chặn gọi connect() trùng bằng ref thay vì state `isConnecting`: nếu `connect` phụ thuộc state này,
  // mỗi lần kết nối thất bại nó đổi identity → effect auto-connect chạy lại → lặp vô hạn (gọi settings/public liên tục).
  const isConnectingRef = useRef(false);
  // Thử lại có chủ đích khi kết nối thất bại: giãn cách tăng dần, có giới hạn số lần.
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectRef = useRef<(forceReconnect?: boolean) => Promise<void>>(async () => {});

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Hẹn lần thử kế tiếp: 5s → 10s → 20s → 40s → 60s rồi dừng hẳn.
  const scheduleRetry = useCallback(() => {
    if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
      console.warn(`[SocketProvider] Giving up after ${MAX_RETRY_ATTEMPTS} retries`);
      return;
    }
    const delay = Math.min(RETRY_BASE_DELAY_MS * 2 ** retryCountRef.current, RETRY_MAX_DELAY_MS);
    retryCountRef.current += 1;
    console.log(`[SocketProvider] Retry ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS} in ${delay / 1000}s`);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      void connectRef.current(true);
    }, delay);
  }, []);

  // Lấy realtime URL từ Cookie (đã được lưu khi login)
  const getRealtimeUrlFromCookie = useCallback((): string | null => {
    const url = CookieUtils.getRealtimeUrl();
    if (url && typeof url === 'string' && url.trim() !== '') {
      console.log('[SocketProvider] Got realtime URL from Cookie:', url);
      return url.trim();
    }
    // Không fallback ở đây, để connect() gọi API nếu cần
    return null;
  }, []);

  // Fetch settings từ API nếu không có trong cookie
  const fetchSettingsFromApi = useCallback(async (): Promise<string | null> => {
    try {
      console.log('[SocketProvider] Fetching settings from API...');
      const response = await axiosInstance.get(`/api/${API_PUBLIC_ROUTER.API_SETTING}`, {
        headers: {
          'x-svc-id': ApiConfig.PROJECT_ID.USER_SERVICE,
          'ProjectId': ApiConfig.PROJECT_ID.USER_SERVICE,
        },
      });

      if (response?.data?.status === 200 && response?.data?.data?.CONFIG_REALTIME_URL) {
        const data = response.data.data;
        const realtimeUrl = data.CONFIG_REALTIME_URL;

        // Lưu vào cookie để lần sau không cần gọi API
        CookieUtils.setCookie('CONFIG_REALTIME_URL', realtimeUrl, 12);
        console.log('[SocketProvider] Got realtime URL from API:', realtimeUrl);

        // Lưu CONFIG_RESOURCE_URL nếu có
        if (data.CONFIG_RESOURCE_URL) {
          CookieUtils.setCookie('CONFIG_RESOURCE_URL', data.CONFIG_RESOURCE_URL, 12);
        }

        return realtimeUrl;
      }

      console.warn('[SocketProvider] API response did not contain CONFIG_REALTIME_URL');
    } catch (err) {
      console.error('[SocketProvider] Failed to fetch settings from API:', err);
    }
    return null;
  }, []);

  // Connect socket với URL từ Cookie hoặc API
  const connect = useCallback(async (forceReconnect: boolean = false) => {
    const token = CookieUtils.getCurrentUser().access_token ?? "";
    if (!token) {
      console.log('[SocketProvider] No auth token, skipping connection');
      return;
    }

    if (isConnectingRef.current) return;
    if (socketService.isConnected() && !forceReconnect) {
      setIsConnected(true);
      return;
    }

    clearRetryTimer();
    isConnectingRef.current = true;
    setIsConnecting(true);
    setError(null);

    try {
      // 1. Thử lấy từ cookie trước
      let url = getRealtimeUrlFromCookie();

      // 2. Nếu không có trong cookie → fetch từ API
      if (!url) {
        console.log('[SocketProvider] No URL in cookie, fetching from API...');
        url = await fetchSettingsFromApi();
      }

      // 3. Fallback localhost trong development (sau khi API cũng fail)
      if (!url && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
        const fallback = 'http://localhost:1181';
        console.warn('[SocketProvider] API failed, using dev fallback:', fallback);
        url = fallback;
      }

      setRealtimeUrl(url);

      if (!url) {
        throw new Error('Could not get realtime URL from cookie or API. User may need to re-login.');
      }

      // 4. Connect socket với URL
      console.log('[SocketProvider] Connecting to socket:', url);
      await socketService.connect(url, forceReconnect);
      setIsConnected(true);
      retryCountRef.current = 0;
      console.log('[SocketProvider] Socket connected successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
      setIsConnected(false);
      console.error('[SocketProvider] Socket connection failed:', err);
      // Huỷ socket vừa thất bại để socket.io không tự reconnect chồng lên lần thử kế tiếp.
      socketService.disconnect();
      scheduleRetry();
    } finally {
      isConnectingRef.current = false;
      setIsConnecting(false);
    }
  }, [getRealtimeUrlFromCookie, fetchSettingsFromApi, clearRetryTimer, scheduleRetry]);

  useEffect(() => { connectRef.current = connect; }, [connect]);

  // Kết nối lại thủ công: bắt đầu lại chuỗi thử từ đầu.
  const reconnect = useCallback(async () => {
    retryCountRef.current = 0;
    await connect(true);
  }, [connect]);

  // Auto connect khi component mount (Layout được load sau đăng nhập)
  useEffect(() => {
    const shouldForceReconnect = !mountedRef.current;
    mountedRef.current = true;

    console.log('[SocketProvider] Initializing socket connection...');
    connect(shouldForceReconnect);

    // Cleanup khi window unload (page refresh/close)
    const handleBeforeUnload = () => {
      socketService.disconnect();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearRetryTimer();
      // Không disconnect khi unmount để duy trì connection
    };
  }, [connect, clearRetryTimer]);

  // Thử lại ngay khi có tín hiệu môi trường thay đổi, không chờ hết thời gian hẹn.
  useEffect(() => {
    // Có mạng trở lại: bắt đầu lại chuỗi thử từ đầu.
    const handleOnline = () => {
      if (socketService.isConnected()) return;
      retryCountRef.current = 0;
      void connect(true);
    };
    // Mở lại tab: chỉ thử thêm 1 lần (không reset bộ đếm) để không lặp vô hạn khi lỗi không tự hết.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || socketService.isConnected()) return;
      void connect(true);
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect]);

  // Lắng nghe sự kiện logout để disconnect socket
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Nếu token bị xóa (logout), disconnect socket
      if (e.key === 'user' && !e.newValue) {
        clearRetryTimer();
        socketService.disconnect();
        setIsConnected(false);
        setRealtimeUrl(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [clearRetryTimer]);

  return (
    <SocketContext.Provider value={{ isConnected, isConnecting, error, realtimeUrl, reconnect }}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
